<script>
import { ref, computed, watch } from '@vue/composition-api';
import {
  BModal,
  BFormFile,
  BAlert,
  BTable,
  BProgress,
  BBadge,
  BSpinner,
  BButton,
} from 'bootstrap-vue';
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue';
import * as XLSX from 'xlsx';
import store from '@/store';

export default {
  name: 'CustomerListImportModal',
  components: {
    BModal,
    BFormFile,
    BAlert,
    BTable,
    BProgress,
    FeatherIcon,
    BBadge,
    BSpinner,
    BButton,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['bulkImport', 'update:show'],
  setup(props, { emit }) {
    const file = ref(null);
    const processing = ref(false);
    const fileProcessing = ref(false);
    const validationErrors = ref([]);
    const previewData = ref([]);
    const progressValue = ref(0);
    const progressText = ref('');

    // Template URL for customer import
    const templateUrl = '/downloads/SalesInvoiceFBR_Customers_Templet.xlsx';

    // Preview table fields
    const previewFields = [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'category', label: 'Category' },
      { key: 'cellNumber', label: 'Cell Number' },
      { key: 'ntnNumber', label: 'NTN Number' },
      { key: 'cnicNumber', label: 'CNIC Number' },
    ];

    // Computed property for modal visibility
    const showModal = computed({
      get: () => props.show,
      set: (value) => emit('update:show', value),
    });

    // Process preview data for display
    const processedPreviewData = computed(() => {
      return previewData.value.map((customer) => ({
        ...customer,
        code: customer.code,
        name: customer.name,
        category: customer.category,
        cellNumber: customer.cellNumber,
        ntnNumber: customer.ntnNumber,
        cnicNumber: customer.cnicNumber,
      }));
    });

    // Calculate totals for summary
    const totalCustomers = computed(() => {
      return previewData.value.length;
    });

    // Watch for file changes to process and validate
    watch(file, async (newFile) => {
      if (newFile) {
        fileProcessing.value = true;
        validationErrors.value = [];
        previewData.value = [];

        try {
          await processExcelFile(newFile);
        } finally {
          fileProcessing.value = false;
        }
      } else {
        previewData.value = [];
        validationErrors.value = [];
      }
    });

    // Process Excel file
    const processExcelFile = async (excelFile) => {
      try {
        const data = await readExcelFile(excelFile);

        if (!data || data.length === 0) {
          validationErrors.value = ['Excel file appears to be empty or invalid.'];
          return;
        }

        console.log('Raw Excel data:', data);
        console.log('Headers:', data[0]);
        console.log('First data row:', data[1]);

        const { customers, errors } = parseExcelData(data);

        validationErrors.value = errors;
        previewData.value = customers;

        if (errors.length === 0) {
          console.log('Parsed customers:', customers);
          console.log('API request body will be:', JSON.stringify(customers, null, 2));
        }
      } catch (error) {
        console.error('Error processing Excel file:', error);
        validationErrors.value = [
          "Error reading Excel file. Please ensure it's a valid Excel file.",
          'Make sure the file follows the template format exactly.',
        ];
      }
    };

    // Read Excel file
    const readExcelFile = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            console.log('Reading Excel file with options...');

            const workbook = XLSX.read(data, {
              type: 'array',
              cellDates: true,
              cellNF: false,
              cellText: false,
            });

            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];

            console.log('Worksheet info:', {
              sheetName,
              range: worksheet['!ref'],
              cellDates: true,
            });

            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              raw: true,
              dateNF: 'yyyy-mm-dd',
              defval: '',
            });

            console.log('Excel reading completed');
            resolve(jsonData);
          } catch (error) {
            console.error('Error reading Excel file:', error);
            reject(error);
          }
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
    };

    // Parse Excel data for customers
    const parseExcelData = (excelData) => {
      if (excelData.length < 2) {
        return {
          customers: [],
          errors: ['Excel file must contain at least a header row and one data row.'],
        };
      }

      const headers = excelData[0];
      const dataRows = excelData.slice(1);
      const errors = [];
      const customers = [];

      // Helper function to find column index with flexible matching
      const findColumnIndex = (headers, possibleNames) => {
        const headerStr = headers.map((h) => (h ? h.toString().toLowerCase().trim() : ''));
        for (const name of possibleNames) {
          const index = headerStr.findIndex((h) => h.includes(name.toLowerCase()));
          if (index !== -1) return index;
        }
        return -1;
      };

      // Find required column indices with flexible matching
      const requiredColumns = {
        code: findColumnIndex(headers, ['code*', 'code', 'customer code*', 'customer code']),
        name: findColumnIndex(headers, [
          'name*',
          'name',
          'customer name*',
          'customer name',
          'full name*',
          'full name',
        ]),
        shortName: findColumnIndex(headers, ['short name', 'shortname', 'abbreviation', 'abbr']),
        commissionPercent: findColumnIndex(headers, [
          'commission %',
          'commission%',
          'commission percent',
          'commission',
        ]),
        category: findColumnIndex(headers, [
          'category*',
          'category',
          'customer category*',
          'customer category',
        ]),
        address: findColumnIndex(headers, ['address', 'customer address', 'location']),
        province: findColumnIndex(headers, ['province*', 'province', 'state*', 'state']),
        gstNumber: findColumnIndex(headers, ['gst number', 'gst', 'gst no', 'gst no.']),
        ntnNumber: findColumnIndex(headers, [
          'ntn number*',
          'ntn number',
          'ntn*',
          'ntn',
          'ntn no',
          'ntn no.',
        ]),
        cnicNumber: findColumnIndex(headers, [
          'cnic number',
          'cnic',
          'cnic no',
          'cnic no.',
        ]),
        contactPerson: findColumnIndex(headers, [
          'contact person',
          'contact',
          'contact name',
          'person',
        ]),
        cellNumber: findColumnIndex(headers, [
          'cell number*',
          'cell number',
          'cell*',
          'cell',
          'mobile*',
          'mobile',
          'phone*',
          'phone',
        ]),
        whatsappOrUAN: findColumnIndex(headers, [
          'whatsapp',
          'whatsapp number',
          'uan',
          'whatsapp/uan',
        ]),
        telephoneNumber: findColumnIndex(headers, [
          'telephone number',
          'telephone',
          'tel',
          'landline',
        ]),
        email: findColumnIndex(headers, ['email', 'email address', 'e-mail']),
        website: findColumnIndex(headers, ['website', 'web', 'url', 'site']),
        remarks: findColumnIndex(headers, ['remarks', 'notes', 'comment', 'comments']),
      };

      console.log('Required columns found:', requiredColumns);

      // Validate required columns exist
      const missingColumns = Object.entries(requiredColumns)
        .filter(
          ([key, index]) =>
            index === -1 &&
            key !== 'code' &&
            key !== 'category' &&
            key !== 'cellNumber' &&
            key !== 'shortName' &&
            key !== 'commissionPercent' &&
            key !== 'address' &&
            key !== 'gstNumber' &&
            key !== 'cnicNumber' &&
            key !== 'contactPerson' &&
            key !== 'whatsappOrUAN' &&
            key !== 'telephoneNumber' &&
            key !== 'email' &&
            key !== 'website' &&
            key !== 'remarks'
        )
        .map(([key]) => key);

      if (missingColumns.length > 0) {
        errors.push(`Missing required columns: ${missingColumns.join(', ')}`);
        errors.push(
          'Please ensure your Excel file contains the required columns. Column names are flexible and can include variations (e.g., "Name", "Customer Name", "Full Name" are all acceptable for the name field).'
        );
        return { customers, errors };
      }

      // Process each row
      dataRows.forEach((row, rowIndex) => {
        if (
          row.length === 0 ||
          !row.some((cell) => cell !== null && cell !== undefined && cell !== '')
        ) {
          return; // Skip empty rows
        }

        // Validate required fields
        const code = row[requiredColumns.code];
        const name = row[requiredColumns.name];
        if (!name) {
          errors.push(`Row ${rowIndex + 2}: Name is required`);
          return;
        }

        const category = row[requiredColumns.category];
        const province = row[requiredColumns.province];
        if (!province) {
          errors.push(`Row ${rowIndex + 2}: Province is required`);
          return;
        }

        const cellNumber = row[requiredColumns.cellNumber];

        // Validate cell number format (92########## or +92##########) - Optional field
        let formattedCellNumber = '';
        
        if (cellNumber && cellNumber.toString().trim() !== '') {
          let cellNumberStr = '';

          // Handle different data types that Excel might return
          if (typeof cellNumber === 'number') {
            // Handle scientific notation (e.g., 9.23042E+11)
            if (cellNumber.toString().includes('E') || cellNumber.toString().includes('e')) {
              // Convert scientific notation back to full number
              cellNumberStr = cellNumber.toLocaleString('fullwide', { useGrouping: false });
            } else {
              cellNumberStr = cellNumber.toString();
            }
          } else if (typeof cellNumber === 'string') {
            cellNumberStr = cellNumber.trim();
          } else {
            cellNumberStr = String(cellNumber).trim();
          }

          let tempFormattedCellNumber = cellNumberStr;

          // Remove any existing + prefix for validation
          if (cellNumberStr.startsWith('+')) {
            tempFormattedCellNumber = cellNumberStr.substring(1);
          }

          // Debug logging
          console.log(`Row ${rowIndex + 2} - Original cell number:`, cellNumber, typeof cellNumber);
          console.log(`Row ${rowIndex + 2} - Formatted cell number:`, tempFormattedCellNumber);

          // Check if it starts with 92 and has 12 digits total (92 + 10 digits)
          const cellNumberRegex = /^92\d{10}$/;
          if (!cellNumberRegex.test(tempFormattedCellNumber)) {
            errors.push(
              `Row ${rowIndex + 2}: Cell Number must start with 92 followed by 10 digits (e.g., 921231234567). Got: ${tempFormattedCellNumber}`
            );
            return;
          }

          // Format the cell number with + prefix for storage
          formattedCellNumber = '+' + tempFormattedCellNumber;
        }

        const ntnNumber = row[requiredColumns.ntnNumber];
        const cnicNumber = row[requiredColumns.cnicNumber];

        // Check if either NTN or CNIC is provided
        const hasNTN = ntnNumber && ntnNumber.toString().trim() !== '';
        const hasCNIC = cnicNumber && cnicNumber.toString().trim() !== '';

        if (!hasNTN && !hasCNIC) {
          errors.push(`Row ${rowIndex + 2}: Either NTN Number or CNIC Number is required.`);
          return;
        }

        // Validate NTN Number if provided
        if (hasNTN) {
          const ntnStr = ntnNumber.toString().trim();
          // Allow 7 digits, 8 digits with dash (1234567-8), or alphanumeric format (A123456 or A123456-7)
          const ntnRegex = /^(\d{7}|\d{7}-\d{1}|[A-Za-z]\d{6}(-\d{1})?)$/;
          if (!ntnRegex.test(ntnStr)) {
            errors.push(`Row ${rowIndex + 2}: NTN Number must be 7 digits, 8 digits with dash (1234567-8), or in format A123456 or A123456-7`);
            return;
          }
        }

        // Validate CNIC Number (13 digits if provided)
        if (hasCNIC) {
          const cnicStr = cnicNumber.toString().trim();
          const cnicRegex = /^\d{13}$/;
          if (!cnicRegex.test(cnicStr)) {
            errors.push(`Row ${rowIndex + 2}: CNIC Number must be exactly 13 digits`);
            return;
          }
        }

        // Validate commission percent (number if provided)
        const commissionPercent = row[requiredColumns.commissionPercent];
        if (
          commissionPercent !== undefined &&
          commissionPercent !== null &&
          commissionPercent !== ''
        ) {
          const commissionValue = parseFloat(commissionPercent);
          if (isNaN(commissionValue) || commissionValue < 0) {
            errors.push(`Row ${rowIndex + 2}: Commission % must be a non-negative number`);
            return;
          }
        }

        // Validate email format (if provided)
        const email = row[requiredColumns.email];
        if (email && email !== '') {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(email.toString())) {
            errors.push(`Row ${rowIndex + 2}: Email must be in valid format`);
            return;
          }
        }

        // Create customer object
        const customer = {
          id: 0,
          businessID: 0, // Will be set from store
          code: code ? code.toString().trim() : '',
          name: name.toString().trim(),
          shortName: row[requiredColumns.shortName]?.toString().trim() || '',
          commissionPercent:
            commissionPercent !== undefined &&
            commissionPercent !== null &&
            commissionPercent !== ''
              ? parseFloat(commissionPercent)
              : 0,
          accountID: 0,
          accountTitle: '',
          category: category ? category.toString().trim() : '',
          address: row[requiredColumns.address]?.toString().trim() || '',
          province: province.toString().trim(),
          gstNumber: row[requiredColumns.gstNumber]?.toString().trim() || '',
          ntnNumber: ntnNumber?.toString().trim() || '',
          cnicNumber: cnicNumber?.toString().trim() || '',
          contactPerson: row[requiredColumns.contactPerson]?.toString().trim() || '',
          cellNumber: formattedCellNumber, // Use the formatted cell number with + prefix
          whatsappOrUAN: row[requiredColumns.whatsappOrUAN]?.toString().trim() || '',
          telephoneNumber: row[requiredColumns.telephoneNumber]?.toString().trim() || '',
          email: email?.toString().trim() || '',
          website: row[requiredColumns.website]?.toString().trim() || '',
          remarks: row[requiredColumns.remarks]?.toString().trim() || '',
          isActive: true,
        };

        customers.push(customer);
      });

      // Validate that we have at least one customer
      if (customers.length === 0) {
        errors.push('No valid customers found in the Excel file');
        return { customers, errors };
      }

      return { customers, errors };
    };

    // Submit for import
    const onSubmit = async () => {
      if (!file.value || previewData.value.length === 0) {
        return;
      }

      // Check if businessID is available
      const businessID = store.state.user.businessID;
      if (!businessID) {
        validationErrors.value = [
          'Business ID not found. Please ensure you are logged in with a valid business account.',
        ];
        return;
      }

      processing.value = true;
      progressValue.value = 0;
      progressText.value = 'Preparing data for import...';

      try {
        // Set businessID for all customers
        const processedData = previewData.value.map((customer) => ({
          ...customer,
          businessID,
        }));

        progressValue.value = 50;
        progressText.value = 'Uploading to server...';

        // Emit the bulk import event with a completion callback
        emit('bulkImport', processedData, (success, error) => {
          if (success) {
            // Success - update progress and close modal
            progressValue.value = 100;
            progressText.value = 'Import completed successfully!';

            // Reset form
            file.value = null;
            previewData.value = [];
            validationErrors.value = [];

            // Close modal after showing success message
            setTimeout(() => {
              showModal.value = false;
            }, 1500);
          } else {
            // Error - show error message
            validationErrors.value = [`Import failed: ${error?.message || 'Unknown error'}`];
            progressValue.value = 0;
            progressText.value = 'Import failed. Please check the errors above.';
          }
          processing.value = false;
        });
      } catch (error) {
        console.error('Import error:', error);
        validationErrors.value = [`Import failed: ${error.message}`];
        progressValue.value = 0;
        progressText.value = 'Import failed. Please check the errors above.';
        processing.value = false;
      }
    };

    // Cancel import
    const onCancel = () => {
      if (processing.value) {
        // Don't allow cancellation during processing
        return;
      }

      file.value = null;
      previewData.value = [];
      validationErrors.value = [];
      showModal.value = false;
    };

    // Hide modal
    const onHide = () => {
      if (processing.value) {
        // Don't allow closing during processing
        return false;
      }

      // Reset form when closing
      file.value = null;
      previewData.value = [];
      validationErrors.value = [];
      return true;
    };

    // Clear form
    const clearForm = () => {
      if (processing.value) {
        // Don't allow clearing during processing
        return;
      }

      file.value = null;
      previewData.value = [];
      validationErrors.value = [];
    };

    return {
      file,
      processing,
      fileProcessing,
      validationErrors,
      previewData,
      processedPreviewData,
      totalCustomers,
      progressValue,
      progressText,
      templateUrl,
      previewFields,
      showModal,
      onSubmit,
      onCancel,
      onHide,
      clearForm,
    };
  },
};
</script>

<template>
  <b-modal
    v-model="showModal"
    title="Import Customers"
    size="lg"
    :ok-disabled="!file || processing || validationErrors.length > 0 || previewData.length === 0"
    :cancel-disabled="processing"
    :hide-header-close="processing"
    @ok="onSubmit"
    @cancel="onCancel"
    @hide="onHide"
    :before-close="onHide"
  >
    <div class="mb-3">
      <p class="text-muted mb-2">
        Please upload an Excel file with the required columns. Column names are flexible and can
        include variations. Required fields are marked with *. <strong>Either NTN Number or CNIC Number is required (you must provide at least one).</strong> Code, Category, and Cell Number are optional. Cell numbers should start with 92 followed by 10 digits (e.g., 921231234567).
      </p>
      <a :href="templateUrl" download class="btn btn-outline-primary btn-sm">
        <feather-icon icon="DownloadIcon" class="mr-1" />
        Download Template
      </a>
    </div>

    <b-form-file
      v-model="file"
      :state="Boolean(file)"
      placeholder="Choose an Excel file or drop it here..."
      drop-placeholder="Drop Excel file here..."
      drop-variant="primary"
      accept=".xlsx,.xls"
      :disabled="processing || fileProcessing"
    />

    <!-- File Processing Indicator -->
    <div v-if="fileProcessing" class="mt-3">
      <div class="text-center">
        <b-spinner small class="mr-2" />
        <span class="text-muted">Processing Excel file...</span>
      </div>
    </div>

    <!-- Validation Errors -->
    <div v-if="validationErrors.length > 0" class="mt-3">
      <b-alert variant="danger" show>
        <div class="d-flex justify-content-between align-items-start p-2">
          <div>
            <h6>Validation Errors:</h6>
            <ul class="mb-0">
              <li v-for="(error, index) in validationErrors" :key="index">
                {{ error }}
              </li>
            </ul>
          </div>
          <b-button variant="outline-danger" size="sm" @click="clearForm" :disabled="processing">
            Clear & Try Again
          </b-button>
        </div>
      </b-alert>
    </div>

    <!-- Preview Data -->
    <div v-if="previewData.length > 0" class="mt-3">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h6 class="mb-0">Preview ({{ previewData.length }} customers):</h6>
        <div class="text-muted">
          <small>Total Customers: {{ totalCustomers }}</small>
        </div>
      </div>
      <div class="table-responsive">
        <b-table
          :items="processedPreviewData"
          :fields="previewFields"
          small
          striped
          hover
          class="mb-0"
          :busy="processing"
        >
          <template #table-busy>
            <div class="text-center">
              <b-spinner small class="mr-2" />
              <span class="text-muted">Processing...</span>
            </div>
          </template>
          <template #cell(code)="data">
            <strong>{{ data.value }}</strong>
          </template>
          <template #cell(name)="data">
            <span class="text-primary">{{ data.value }}</span>
          </template>
          <template #cell(category)="data">
            <b-badge variant="info">{{ data.value }}</b-badge>
          </template>
          <template #cell(cellNumber)="data">
            <span class="font-weight-bold">{{ data.value }}</span>
          </template>
          <template #cell(ntnNumber)="data">
            <span v-if="data.value" class="text-success">{{ data.value }}</span>
            <span v-else class="text-muted font-italic">Not provided</span>
          </template>
          <template #cell(cnicNumber)="data">
            <span v-if="data.value" class="text-success">{{ data.value }}</span>
            <span v-else class="text-muted font-italic">Not provided</span>
          </template>
        </b-table>
      </div>
    </div>

    <!-- Progress Bar -->
    <div v-if="processing" class="mt-3">
      <b-alert variant="info" show>
        <div class="d-flex align-items-center">
          <b-spinner small class="mr-2" />
          <span
            ><strong>Upload in Progress:</strong> Please wait while your customers are being
            uploaded. The modal will close automatically when complete.</span
          >
        </div>
      </b-alert>
      <b-progress :value="progressValue" :max="100" animated />
      <small class="text-muted">{{ progressText }}</small>
    </div>

    <!-- Custom Footer -->
    <template #modal-footer>
      <div v-if="!processing" class="w-100 d-flex justify-content-end">
        <b-button variant="secondary" @click="onCancel" class="mr-2"> Cancel </b-button>
        <b-button
          variant="primary"
          @click="onSubmit"
          :disabled="!file || validationErrors.length > 0 || previewData.length === 0"
        >
          Import
        </b-button>
      </div>
      <div v-else class="w-100 text-center">
        <b-spinner small class="mr-2" />
        <span class="text-muted">Uploading customers to server...</span>
      </div>
    </template>
  </b-modal>
</template>

<style lang="scss" scoped>
.table-responsive {
  max-height: 300px;
  overflow-y: auto;
}
</style>
